import { type Worklog } from '@forecastify/shared';
import type { DataListColumn, DataListRow } from '@forecastify/ui';
import type { Translate } from '../../i18n';
import { personCell } from '../person-cell';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A worklog entry, as a row of the product's own list. Two callers.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Extracted for C27.2 out of `logs-screen.tsx`, for `task-columns.ts`' reason exactly**: the
 * agent's reply renders the entries it read, and it renders them with the mapping the Idolog list
 * already uses. A second one would be a second place for `formatHours` to be forgotten — and the
 * failure is a chat that reports `6.5` hours where every screen says `6:30`.
 */

export const WORKLOG_COLUMNS = [
  'date',
  'task',
  'note',
  'project',
  'member',
  /* P3-5 · what an hour was claimed as, and at what rate. Between the project and the figure,
     which is where the mockup puts it — beside the number the multiplier applies to. */
  'overtime',
  'hours',
] as const;
export type WorklogColumnKey = (typeof WORKLOG_COLUMNS)[number];

/**
 * ⚠️⚠️ **MY OWN HOURS, SO NO *FELELŐS* COLUMN — the ruling of 20 Sep.** Every row on the
 * personal list is the reader's, and a column repeating their own name on all fifty of them is
 * width spent saying nothing. The workspace-wide list keeps it, because there it is the question.
 */
export const MY_WORKLOG_COLUMNS: readonly WorklogColumnKey[] = WORKLOG_COLUMNS.filter(
  (key) => key !== 'member',
);

/**
 * ⚠️⚠️ **THE CHAT SHOWS `member` AND THE PERSONAL LOGS SCREEN DOES NOT.** A reply has no
 * toolbar and no scope: *"who logged this"* is exactly the thing a person cannot otherwise tell,
 * and `worklogs_search` can be asked for everybody's hours.
 */
export const CHAT_WORKLOG_COLUMNS: readonly WorklogColumnKey[] = [
  'date',
  'task',
  'member',
  'note',
  'hours',
];

/** The mockup's own list columns, in its order and with its widths. */
export function worklogColumns(
  t: Translate,
  keys: readonly WorklogColumnKey[] = WORKLOG_COLUMNS,
): DataListColumn[] {
  return keys.map((key) => {
    /* ⚠️ Every `sortKey` is a name in `WORKLOG_SORT`, the server's catalogue (item 45, D3). A key
       this list invents renders a header button that reorders nothing. */
    if (key === 'date') {
      return { key, label: t('time.column.date'), width: 120, sortKey: 'worked_on' };
    }
    if (key === 'task') {
      return { key, label: t('time.column.task'), width: 120, sortKey: 'task' };
    }
    if (key === 'note') return { key, label: t('time.column.note'), sortKey: 'note' };
    if (key === 'project') {
      return { key, label: t('projects.column.client'), width: 190, sortKey: 'project' };
    }
    if (key === 'member') {
      return { key, label: t('project.tasks.column.assignee'), width: 150, sortKey: 'member' };
    }
    /* ⚠️ **No `sortKey`.** `WORKLOG_SORT` is the server's catalogue and it has no overtime entry;
       a header button here would reorder nothing and say it had. */
    if (key === 'overtime') return { key, label: t('time.column.overtime'), width: 190 };
    /* ⚠️ Sorts on the NUMERIC hours, not the rendered `H:MM` — `10:00` sorts before `2:00` as
       text, which is the classic way an hours column reads as broken. `WORKLOG_SORT` says so. */
    return { key, label: t('time.column.hours'), width: 100, align: 'right', sortKey: 'hours' };
  });
}

/**
 * ⚠️ 3 Nov 2026 · `fmt` is `useDuration().fmt` — the reader's hours or man-days (`decisions.md`
 * DU-1). A parameter, because this is not a component and cannot call the hook.
 */
export function worklogRows(
  t: Translate,
  entries: readonly Worklog[],
  fmt: (hours: number | string) => string,
): DataListRow[] {
  return entries.map((entry) => ({
    id: entry.id,
    cells: {
      date: { text: entry.workedOn, mono: true, muted: true },
      task: { text: entry.taskKey, mono: true },
      note: { text: entry.note ?? '–', muted: entry.note === null },
      project: {
        dot: entry.projectColor ?? undefined,
        text: entry.projectName ?? t('time.noProject'),
        muted: true,
      },
      member: personCell(entry.memberName, { seed: entry.memberAvatarSeed }),
      /**
       * ⚠️⚠️ **A BADGE CARRYING THE FROZEN RATE, AND THE TYPE'S NAME BESIDE IT.** The
       * multiplier is the one the claim will actually be paid at — agreed at approval — so the row
       * answers *"what is this hour worth"* rather than *"what does the type say today"*.
       *
       * ⚠️⚠️ **AND THE STATUS ALONE UNTIL THERE IS ONE — `0147`.** This badge read
       * *Függőben · 2×* on a claim nobody had decided, off the type's current figure, which is the
       * thing the product owner reported: a rate shown as if it were settled. An undecided claim
       * has no kind and therefore no rate, so the badge says only that it is waiting.
       *
       * ⚠️ An en-dash for an ordinary hour: most hours are ordinary, and a blank column reads as
       * missing data.
       */
      /* ⚠️ 27 Oct 2026 · a withdrawn claim reads as the ordinary hour it is again. */
      overtime:
        entry.overtime === null || entry.overtime.status === 'revoked'
          ? { text: '–', muted: true }
          : {
              badge: {
                label:
                  entry.overtime.multiplier === null
                    ? overtimeStatusLabel(t, entry.overtime.status)
                    : `${overtimeStatusLabel(t, entry.overtime.status)} · ${String(
                        Number(entry.overtime.multiplier),
                      )}×`,
                variant: entry.overtime.status === 'approved' ? 'secondary' : 'default',
              },
              text: entry.overtime.typeName ?? '–',
            },
      /* ⚠️ `H:MM` or man-days, from the one function that formats durations in this product. */
      hours: { text: fmt(entry.hours), mono: true, bold: true },
    },
  }));
}

/** ⚠️ One `t()` per branch with a LITERAL key — the i18n rule. */
function overtimeStatusLabel(
  t: Translate,
  status: 'pending' | 'approved' | 'rejected' | 'revoked',
): string {
  if (status === 'approved') return t('leave.status.approved');
  if (status === 'rejected') return t('leave.status.rejected');
  if (status === 'revoked') return t('leave.status.revoked');
  return t('leave.status.pending');
}
